/* eslint-disable no-bitwise, global-require */
import dbg from 'debug';
import { notify } from '../BugsnagClient';

const debug = dbg('jp:analytics');

let instance = null;

const prodConfig = {
  AdWords: {
    conversionId: '1065433872'
  },
  'Facebook Pixel': {
    pixelId: '540636262740875'
  },
  'Google Analytics': {
    trackingId: 'UA-11508076-1'
  },
  'Twitter Ads': {
    page: 'l4swr'
  }
};

const stagingConfig = {
  AdWords: { conversionId: '1065433872' },
  'Facebook Pixel': { pixelId: '540636262740875' },
  'Google Analytics': { trackingId: 'UA-11508076-6' },
  'Twitter Ads': { page: 'l4swr' }
};

if (__CLIENT__) {
  window.jpAnalyticsConfig =
    process.env.RELEASE_STAGE === 'production' ? prodConfig : stagingConfig;
}

const waitForCookiePolicy = () =>
  new Promise((resolve) => {
    if (!('OptanonActiveGroups' in window)) {
      debug(
        'Cookie banner not loaded or not enabled, so ready to load analytics'
      );
      resolve();
    }

    if (document.cookie.indexOf('OptanonAlertBoxClosed=') >= 0) {
      // Optanon cookie found, use has either accepted or rejected cookies
      debug('Cookie found, so ready to load analytics');
      resolve();
    }

    function OptanonWrapper() {
      // eslint-disable-next-line no-undef,new-cap
      Optanon.OnConsentChanged(() => {
        debug('Cookie changed, so ready to load analytics');
        resolve();
      });
    }

    window.OptanonWrapper = OptanonWrapper;
  });

export default class Analytics {
  constructor() {
    if (instance) {
      return instance;
    }

    this.defaultProperties = {};
    this.tracking = null;

    if (!instance && __CLIENT__ && process.env.NODE_ENV !== 'test') {
      instance = this;
      return instance;
    }

    return {
      page: () => {},
      identify: () => {},
      track: () => {},
      reset: () => {},
      setDefaultProperties: () => {}
    };
  }

  getTracking() {
    if (this.tracking !== null) {
      return this.tracking;
    }

    this.tracking = waitForCookiePolicy()
      .then(() => import(/* webpackChunkName: "jp" */ 'jp-analytics'))
      .then(({ default: analytics }) =>
        analytics(process.env.JP_ANALYTICS_CLIENT_ID, {
          JustPark: {
            baseUrl: process.env.JP_ANALYTICS_URL
          }
        })
      )
      .then(
        (tracking) =>
          new Promise((resolve) => {
            resolve(tracking);

            if (typeof tracking.ready !== 'function') {
              return;
            }

            if ('ga' in window) {
              // Preload ecommerce plugin
              window.ga('require', 'ecommerce');
            }

            tracking.ready(() => {
              // Do any other setup required
              // HEADS UP! Is there is an ad blocker on the page then this ready callback will not be called
              debug('ready');
            });
          })
      )
      .catch((err) => {
        notify(err);
        return null;
      });

    return this.tracking;
  }

  setDefaultProperties(properties = {}) {
    debug('defaultProperties', { properties });

    this.defaultProperties = {
      ...this.defaultProperties,
      ...properties
    };

    if ('dataLayer' in window) {
      window.dataLayer.push({
        defaultProperties: this.defaultProperties
      });
    }
  }

  identify(userId = '', traits = {}, options = {}, callback = () => {}) {
    this.getTracking()
      .then((tracking) => {
        if (tracking === null) {
          callback();
          return;
        }

        debug('identify', { userId, traits, options });

        tracking.identify(userId, traits, options, callback);
      })
      .catch((err) => {
        callback();
        notify(err);
      });
  }

  track(event, properties = {}, options = {}, callback = () => {}) {
    const props = {
      ...properties,
      ...this.defaultProperties,
      date: ~~(Date.now() / 1000)
    };

    this.getTracking()
      .then((tracking) => {
        if (tracking === null) {
          callback();
          return;
        }

        debug('event', { event, props, options });
        if ('dataLayer' in window) {
          window.dataLayer.push({
            event,
            eventType: 'customJPEvent',
            eventData: properties,
            _clear: true
          });
        }
        tracking.track(event, props, options, callback);
      })
      .catch((err) => {
        callback();
        notify(err);
      });
  }

  alias(id) {
    this.getTracking().then((tracking) => {
      if (tracking === null) {
        return;
      }

      debug('aliasing user', {
        to: id
      });

      tracking.alias(id);
    });
  }

  page(name = '', properties = {}, options = {}) {
    const props = {
      ...properties,
      ...this.defaultProperties,
      date: ~~(Date.now() / 1000)
    };

    this.getTracking().then((tracking) => {
      if (tracking === null) {
        return;
      }

      debug('page', { name, props, options });
      if ('dataLayer' in window) {
        window.dataLayer.push({
          event: `page:${name}`,
          eventType: 'page',
          eventData: properties
        });
      }

      tracking.page(name, props, options);
    });
  }

  reset() {
    if (__CLIENT__) {
      this.getTracking().then((tracking) => {
        if (tracking === null) {
          return;
        }

        debug('reset');
        tracking.reset();
      });
    }
  }
}

// Analytics singleton
const analytics = new Analytics();

export function track(
  event,
  properties = {},
  options = {},
  callback = () => {}
) {
  analytics.track(event, properties, options, callback);
}

export function page(name, properties = {}, options = {}) {
  analytics.page(name, properties, options);
}

export function gtag(args) {
  if (__CLIENT__ && 'dataLayer' in window) {
    window.dataLayer.push({
      ...args
    });
  }
}

/**
 * Track function that just pushed to the data layer
 * There is a problem with out track function in the Analytics class
 * Because it uses a promise, the racking code is not called immediately even if the promise is resolved
 * Instead it's called during the next tick. So tracking calls can be called late, which s an issue if we are calling track before a page change
 * as by the time track has been called the URL is different to the page that it was called on
 */
export function gtagTrack(event, args = {}) {
  // https://www.simoahava.com/analytics/two-simple-data-model-tricks/
  // Push clear to prevent recursive merging of the items arrays and eventData object
  gtag({ event, eventData: { ...args }, _clear: true });
}
